import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store/types";
import {
  getProjects,
  getProjectById,
  getProjectAreas,
  getProjectAttachments,
  getProjectAreaAttachments,
  postProjectAreaAttachments,
  postTagAttachments,
  postProjectAttachments,
  deleteProjectAttachment,
  getProjectPhases,
  getProjectIssues,
  editProjectMember,
  changeProjectMemberAccess,
  uploadTagSheetCSV,
  downloadTagSheetCSVTemplate,
  verifyTagSheetCSV,
  exportTagSheetCSV,
  exportIssuesList,
  createProject,
  assignTagToTechs,
  getKpiById,
  inviteTeamMember,
  closeProject,
  deleteProject,
  assignAreaToTechs,
  createPhase,
  createChecklist,
  resetError,
  updateProjectIssue,
  getTagById,
  getProjectHandoverLink,
  updateChecklist,
  deleteChecklist,
  getChecklistsAvailableForCopying,
  copyChecklist,
  updatePhaseName,
  generateChecklistReport,
  rejectChecklist,
  createOrUpdateExclusionList,
  updateProjectTag,
  completedChecklistPackageLink,
  generateCompletedChecklistPackage,
  updateUpdateableFlexFields,
  getFlexFieldById,
  exportMultipleTagSheetsXLSX,
  deleteAreaAttachment,
  postProjectAreaImage,
  deleteAreaImage,
  getProjectAreaImages,
} from "./actions";
import { ProjectsActions, AdminProjectsState, CSVExportRequest, CSVOneFileExportRequest } from "./types";
import { formatArea } from "./formats";
import {
  ApiV1ProjectsAreaAttachmentsDeleteRequest,
  ApiV1ProjectsAreaAttachmentsGetRequest,
  ApiV1ProjectsIdAreasAttachmentPostRequest,
  ApiV1ProjectsAttachmentsDeleteRequest,
  ApiV1ProjectsIdPutRequest,
  ApiV1ProjectsIdAttachmentPostRequest,
  ApiV1ProjectsIdAreasImagePostRequest,
  ApiV1ProjectsAreaImagesGetRequest,
  ApiV1ProjectsAreaImageDeleteRequest,
  ApiV1TagAttachmentPostRequest,
} from "../../api";

export const useProjectAreas = () =>
  useSelector((state: AppState) => {
    if (state.admin.projects.project!.areas) {
      return {
        areas: state.admin.projects.project.areas.map(formatArea),
        isLoading: state.admin.projects.isLoadingProjectAreas,
      };
    }
    return {
      isLoading: false,
      areas: [],
    };
  });

export const useProjectPhases = () =>
  useSelector((state: AppState) => {
    if (state.admin.projects.project!.phases) {
      return {
        phases: state.admin.projects.project.phases,
        isLoading: state.admin.projects.isLoadingProjectPhases,
      };
    }
    return {
      isLoading: false,
      phases: [],
    };
  });

export const useProjectIssues = () =>
  useSelector((state: AppState) => {
    if (state.admin.projects.issues) {
      return {
        issues: state.admin.projects.issues,
        isLoading: state.admin.projects.isLoadingProjectIssues,
        isUpdatingIssue: state.admin.projects.isUpdatingIssue,
        isIssueUpdated: state.admin.projects.isIssueUpdated,
      };
    }
    return {
      isLoading: false,
      issues: [],
    };
  });

export const useProjectsState = () =>
  useSelector((state: AppState) => {
    return state.admin.projects;
  });

export const useProjectsActions = () => {
  const dispatch = useDispatch();
  const actions: ProjectsActions = useMemo(
    () => ({
      getProjects: () => dispatch(getProjects()),
      resetError: () => dispatch(resetError()),
      getProjectById: (id) => dispatch(getProjectById(id)),
      getKpiById: (kpiRequest) => dispatch(getKpiById(kpiRequest)),
      getProjectIssues: (issuesRequest) => dispatch(getProjectIssues(issuesRequest)),
      createProject: (newProjectRequest) => dispatch(createProject(newProjectRequest)),
      editProjectMember: (request) => dispatch(editProjectMember(request)),
      getProjectAreas: (projectId) => dispatch(getProjectAreas(projectId)),
      getProjectAttachments: (projectId: number) => dispatch(getProjectAttachments(projectId)),
      getProjectAreaAttachments: (request: ApiV1ProjectsAreaAttachmentsGetRequest) =>
        dispatch(getProjectAreaAttachments(request)),
      getProjectAreaImages: (request: ApiV1ProjectsAreaImagesGetRequest) => dispatch(getProjectAreaImages(request)),
      postProjectAreaAttachments: (request: ApiV1ProjectsIdAreasAttachmentPostRequest) =>
        dispatch(postProjectAreaAttachments(request)),
      postTagAttachments: (request: ApiV1TagAttachmentPostRequest) => dispatch(postTagAttachments(request)),
      postProjectAreaImage: (request: ApiV1ProjectsIdAreasImagePostRequest) => dispatch(postProjectAreaImage(request)),
      postProjectAttachments: (request: ApiV1ProjectsIdAttachmentPostRequest) =>
        dispatch(postProjectAttachments(request)),
      deleteProjectAttachment: (request: ApiV1ProjectsAttachmentsDeleteRequest) =>
        dispatch(deleteProjectAttachment(request)),
      deleteAreaImage: (request: ApiV1ProjectsAreaImageDeleteRequest) => dispatch(deleteAreaImage(request)),
      getProjectPhases: (projectId) => dispatch(getProjectPhases({ id: projectId })),
      changeProjectMemberAccess: (request) => dispatch(changeProjectMemberAccess(request)),
      uploadTagSheetCSV: (request) => dispatch(uploadTagSheetCSV(request)),
      downloadTagSheetCSVTemplate: () => dispatch(downloadTagSheetCSVTemplate()),
      verifyTagSheetCSV: (csvString) => dispatch(verifyTagSheetCSV(csvString)),
      exportTagSheetCSV: (tagRows) => dispatch(exportTagSheetCSV(tagRows)),
      exportIssuesList: (tagRows) => dispatch(exportIssuesList(tagRows)),
      exportMultipleTagSheetsXLSX: (tagRows: CSVOneFileExportRequest | undefined) =>
        dispatch(exportMultipleTagSheetsXLSX(tagRows)),
      assignTagToTechs: (data) => dispatch(assignTagToTechs(data)),
      inviteTeamMember: (request) => dispatch(inviteTeamMember(request)),
      closeProject: (projectId) => dispatch(closeProject(projectId)),
      deleteProject: (projectId) => dispatch(deleteProject(projectId)),
      assignAreaToTechs: (request) => dispatch(assignAreaToTechs(request)),
      createPhases: (request) => dispatch(createPhase(request)),
      createChecklist: (request) => dispatch(createChecklist(request)),
      updateChecklist: (request) => dispatch(updateChecklist(request)),
      deleteChecklist: (request) => dispatch(deleteChecklist(request)),
      deleteAreaAttachment: (request: ApiV1ProjectsAreaAttachmentsDeleteRequest) =>
        dispatch(deleteAreaAttachment(request)),
      updateProjectIssue: (request) => dispatch(updateProjectIssue(request)),
      getTagById: (request) => dispatch(getTagById(request)),
      getProjectHandoverLink: (projectId) => dispatch(getProjectHandoverLink(projectId)),
      getChecklistsAvailableForCopying: (projectId) => dispatch(getChecklistsAvailableForCopying(projectId)),
      copyChecklist: (request) => dispatch(copyChecklist(request)),
      updatePhaseName: (request) => dispatch(updatePhaseName(request)),
      generateChecklistReport: () => dispatch(generateChecklistReport()),
      rejectChecklist: (request) => dispatch(rejectChecklist(request)),
      createOrUpdateExclusionList: (request) => dispatch(createOrUpdateExclusionList(request)),
      updateProjectTag: (request) => dispatch(updateProjectTag(request)),
      completedChecklistPackageLink: (request) => dispatch(completedChecklistPackageLink(request)),
      generateCompletedChecklistPackage: (request) => dispatch(generateCompletedChecklistPackage(request)),
      updateUpdateableFlexFields: (request: ApiV1ProjectsIdPutRequest) => dispatch(updateUpdateableFlexFields(request)),
      getFlexFieldsById: (id: number) => dispatch(getFlexFieldById({ id })),
    }),
    [dispatch]
  );
  return actions;
};

export const useProjects = (): [AdminProjectsState, ProjectsActions] => {
  return [useProjectsState(), useProjectsActions()];
};
